import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { parse } from 'query-string';
import styled from 'styled-components';
import { getCandidateId, getIntegrations } from '../../../core/coreApi';
import { useAsyncAction } from '../../../commonComponents/Hooks';
import { displayContents, jobAdder } from '../../../core/constants';
import Loading from '../../../commonComponents/Loading';
import { hasValue, getPhoneNumberPartsForNoCountryCode } from '../../../lib/utils';
import {
	BullhornCandidateData,
	getJobAdderApplicationDetails,
	getThirdPartyCandidateDetails,
	getThirdPartyIntegration
} from '../../../core/thirdPartyIntegrations';
import { conditionallyUpdateCacheAndGetSelectedSubCustomerId, getRole, logOut } from '../../../core/session';
import { JobAdderCandidate } from '../../../models/be/JobAdderResults';
import { getClientCustomer } from '../../../core/customerApi';
import Customer from '../../../models/be/Customer';
import ApiException from '../../../lib/ApiException';

const getExistingCandidateId = (candidateIdPromise: Promise<string>): Promise<string> =>
	candidateIdPromise.catch((e: ApiException) => {
		if (e?.status === 404) return null;
		throw e;
	});

const ThirdPartyIntegrations = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [, , performAsyncAction] = useAsyncAction();
	const [integrationId, setIntegrationId] = React.useState<string>();
	const [integrationError, setIntegrationError] = React.useState<boolean>(false);
	const [customer, setCustomer] = React.useState<Customer>(null);
	const queryParams = parse(location.search);
	const entityId = queryParams.EntityID as string;
	const entityType = queryParams.EntityType as string;
	const integration = location.pathname.split('/')[2];
	const loggedIn = !!getRole();
	const customerId = loggedIn ? conditionallyUpdateCacheAndGetSelectedSubCustomerId() : null;

	React.useEffect(() => {
		if (!loggedIn) {
			logOut();
			return;
		}

		performAsyncAction(async () => {
			await getThirdPartyIntegrationId();
			await getCustomerData();
		});
	}, []);

	React.useEffect(() => {
		if (!loggedIn) return;

		performAsyncAction(async () => {
			if (integrationId) {
				if (entityType?.toLowerCase() === 'candidate') {
					await getCandidate();
				} else if (entityType?.toLowerCase() === 'application') {
					await getCandidateFromApplication();
				}
			}
		});
	}, [integrationId]);

	const getThirdPartyIntegrationId = async () => {
		const externalIntegrations = await getIntegrations();
		const thirdPartyIntegrationId = externalIntegrations?.find((x) =>
			x.integrationUrl?.toLowerCase().includes(integration?.toLowerCase())
		).integrationId;
		setIntegrationId(thirdPartyIntegrationId);
	};

	const getCustomerData = async () => {
		const abortController = new AbortController();
		const customerData = await getClientCustomer(customerId, abortController.signal);
		setCustomer(customerData as Customer);
	};

	const getCandidateFromApplication = async () => {
		if (entityType && entityType?.toLowerCase() === 'application') {
			// Get candidate details from application
			const applicationId = entityId;
			const userIntegration = await getThirdPartyIntegration(Number(integrationId));
			const applicationDetail = await getJobAdderApplicationDetails(applicationId, userIntegration);

			const existingCandidateId = applicationDetail
				? await getExistingCandidateId(
						getCandidateId(applicationDetail?.candidate?.candidateId.toString(), integrationId as string)
				  )
				: null;
			const route = getJobAdderRoute(existingCandidateId, applicationDetail.candidate);
			navigate(displayContentsOnlyRoute(route));
		} else {
			navigate(`/candidates?${displayContents}`);
		}
	};

	const getCandidate = async () => {
		const existingCandidateId = await getExistingCandidateId(getCandidateId(entityId, integrationId as string));
		if (entityId && entityType?.toLowerCase() === 'candidate') {
			const userIntegration = await getThirdPartyIntegration(Number(integrationId));

			if (userIntegration?.accessToken) {
				const candidate = !hasValue(existingCandidateId)
					? await getThirdPartyCandidateDetails(entityId, integration, userIntegration)
					: null;

				if (!hasValue(existingCandidateId) && !hasValue(candidate)) setIntegrationError(true);

				const route =
					integration === jobAdder
						? getJobAdderRoute(existingCandidateId, candidate)
						: getBullhornIntegrationRoute(existingCandidateId, candidate?.data);
				navigate(displayContentsOnlyRoute(route));
			} else {
				setIntegrationError(true);
			}
		} else {
			navigate(`/candidates?${displayContents}`);
		}
	};

	const displayContentsOnlyRoute = (route: string) => {
		const separator = route.includes('?') ? '&' : '?';
		return `${route}${separator}integration=${integration}&${displayContents}`;
	};

	const getBullhornIntegrationRoute = (existingCandidateId: string, bullhornCandidateData: BullhornCandidateData) => {
		if (existingCandidateId && integrationId) {
			switch (location.pathname?.toLowerCase()) {
				case '/integration/bullhorn/candidate-create':
					return `/candidates/${existingCandidateId}/checks/add`;
				case '/integration/bullhorn/candidate-report':
					return `/candidates/${existingCandidateId}/report`;
				case '/integration/bullhorn/candidate-profile':
				case '/integration/bullhorn/':
				case '/integration/bullhorn':
					return `/candidates/${existingCandidateId}/details`;
				default:
					return `/candidates`;
			}
		} else {
			let countryCode = '';
			if (bullhornCandidateData?.mobile) {
				const phoneNumberParts = getPhoneNumberPartsForNoCountryCode(
					bullhornCandidateData.mobile,
					customer?.countryISOCode
				);
				countryCode = `&countryCode=${phoneNumberParts.countryCode}`;
			}

			const candidateData = bullhornCandidateData
				? `&firstName=${bullhornCandidateData?.firstName}&middleNames=${bullhornCandidateData?.middleName}
		&lastName=${bullhornCandidateData?.lastName}&dateOfBirth=${bullhornCandidateData?.dateOfBirth}
		&email=${bullhornCandidateData?.email}${countryCode}&mobile=${bullhornCandidateData?.mobile?.replace(
						/[+()\-\sx]/g,
						''
				  )}`
				: '';
			const createCandidate = `/candidates/create?externalCandidateId=${entityId}&integrationId=${integrationId}`;

			return `${createCandidate}${candidateData}`;
		}
	};

	const getJobAdderRoute = (existingCandidateId: string, jobadderCandidateData: JobAdderCandidate) => {
		if (existingCandidateId && integrationId) {
			switch (location.pathname?.toLowerCase()) {
				case '/integration/jobadder/candidate-create':
					return `/candidates/${existingCandidateId}/checks/add`;
				case '/integration/jobadder/candidate-report':
					return `/candidates/${existingCandidateId}/report`;
				case '/integration/jobadder/candidate-profile':
				case '/integration/jobadder/':
				case '/integration/jobadder':
					return `/candidates/${existingCandidateId}/details`;
				default:
					return `/candidates`;
			}
		} else {
			const candidateDateOfBirth = jobadderCandidateData?.dateOfBirth && formatDate(jobadderCandidateData.dateOfBirth);
			const candidateData = jobadderCandidateData
				? `&firstName=${jobadderCandidateData?.firstName}&lastName=${jobadderCandidateData?.lastName}${
						candidateDateOfBirth ? `&dateOfBirth=${candidateDateOfBirth}` : ''
				  }&email=${jobadderCandidateData?.email}&countryCode=AU&mobile=${jobadderCandidateData?.mobile?.replace(
						/[-()x ]/g,
						''
				  )}`
				: '';
			const createCandidate = `/candidates/create?externalCandidateId=${jobadderCandidateData?.candidateId}&integrationId=${integrationId}`;
			return `${createCandidate}${candidateData}`;
		}
	};

	return integrationError ? (
		<StyledBullhornIntegrationError>
			{integration} integration is not currently set up properly. Please speak to your VETTING.com account manager.
		</StyledBullhornIntegrationError>
	) : (
		<Loading />
	);
};

export default ThirdPartyIntegrations;

const StyledBullhornIntegrationError = styled.h3`
	text-align: center;
	margin-top: 25%;
`;

export const formatDate = (date: Date | null): string => {
	if (!date) {
		return null;
	}

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	// Check for the default date "0001-01-01"
	if (year === 1 && month === '01' && day === '01') {
		return null;
	}

	return `${year}-${month}-${day}`;
};
